import "styles/global.scss";
import "styles/nprogress.scss";
import "react-datepicker/dist/react-datepicker.css";
import "leaflet/dist/leaflet.css";
import "../styles/customDatePicker.css";
import React, { ReactElement, useEffect } from "react";
import { NextPage } from "next";
import Head from "next/head";
import { DefaultSeo } from "next-seo";
import Script from "next/script";
import Router, { useRouter } from "next/router";
import NProgress from "nprogress";
import { Toaster } from "components/atoms/Toaster";
import { AnimatePresence } from "framer-motion";
import { wrapper } from "store";
import { useDispatch } from "react-redux";
import { LayoutDashboard } from "components/layouts/LayoutDashboard";
import { Error } from "components/atoms/Error";
import { ErrorBoundary } from "components/atoms/ErrorBoundary";
import { ConfigEnvironmentSchema } from "config/config.schema";
import { cleanEnv } from "envalid";
import { Auth0Provider, withAuthenticationRequired } from "@auth0/auth0-react";
import { fetchCountriesThunk } from "store/countriesSlice/index.slice";
import dynamic from "next/dynamic";
import { fetchCurrenciesThunk } from "store/currenciesSlice/index.slice";
import { LayoutAuthenticated } from "components/layouts/LayoutAuthenticated";
import {
  getLocalStorageItem,
  LocalStorageKeys,
} from "utils/react/localStorage";
import { setFilterSidebarOpenStatus } from "store/tableSlice/index.slice";
import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import { Environments, getEnvironment } from "utils/getEnvironment";
import { Center } from "components/atoms/Center";
import { setCurrentTimeUTC } from "store/tickerSlice/index.slice";
import { useInterval } from "react-use";
import { ReduxThemeProvider } from "components/atoms/ReduxThemeProvider";
import { Maintenance } from "components/pages/Maintenance";
import { Analytics } from "@vercel/analytics/next";

Router.events.on("routeChangeStart", () => NProgress.start());
Router.events.on("routeChangeComplete", () => NProgress.done());
Router.events.on("routeChangeError", () => NProgress.done());

// Loaded lazily: Modals statically imports every modal tree in the app, which
// would otherwise all land in the shared first-load bundle. Nothing renders
// until a modal is opened, so the deferred chunk is invisible to users.
const Modals = dynamic(
  () => import("components/organisms/Modals").then((mod) => mod.Modals),
  { ssr: false },
);

type AppLayoutProps = {
  Component: PageWithLayoutType;
  pageProps: any;
};

type PageWithLayoutType = NextPage & {
  getLayout(component: JSX.Element): React.ReactNode;
  isPublic?: boolean;
};

type PageWrapperProps = { page: ReactElement };

// The wrapper component's identity must be stable across renders: creating a
// fresh component type (or a fresh withAuthenticationRequired HOC) inside the
// render body makes React unmount and remount the entire page tree on every
// CustomApp re-render, including query-only URL changes such as the Shipments
// status tabs, which refires every mount-time data fetch on the page.
const pageWrapperCache = new WeakMap<
  PageWithLayoutType,
  React.ComponentType<PageWrapperProps>
>();

function getPageWrapper(Component: PageWithLayoutType) {
  let Wrapper = pageWrapperCache.get(Component);
  if (!Wrapper) {
    const Inner = ({ page }: PageWrapperProps) =>
      Component.getLayout ? (
        <>{Component.getLayout(page)}</>
      ) : (
        <LayoutDashboard>{page}</LayoutDashboard>
      );
    Wrapper =
      Component.isPublic === true ? Inner : withAuthenticationRequired(Inner);
    pageWrapperCache.set(Component, Wrapper);
  }
  return Wrapper;
}

// See: https://github.com/bugsnag/bugsnag-js/issues/1638
if (!(Bugsnag as any)._client)
  Bugsnag.start({
    apiKey: "b3b66f44dddd4c8d1e42f18de547d16b",
    plugins: [new BugsnagPluginReact()],
    enabledReleaseStages: [Environments.production, Environments.staging],
    releaseStage: getEnvironment(),
  });

const BugsnagErrorBoundary =
  Bugsnag && Bugsnag.getPlugin("react").createErrorBoundary(React);

function CustomApp({ Component, pageProps }: AppLayoutProps) {
  const router = useRouter();

  const dispatch = useDispatch();

  // Validate .env
  if (typeof window === "undefined")
    cleanEnv(process.env, ConfigEnvironmentSchema);

  const isPublic = Component.isPublic === true;

  const PageWrapper = getPageWrapper(Component);

  // This data is required both for logged in users and those that are not, so fetching here.
  useEffect(() => {
    dispatch(fetchCountriesThunk());
    dispatch(fetchCurrenciesThunk());
  }, []);

  useEffect(() => {
    const isTableFilterVisible =
      JSON.parse(
        getLocalStorageItem(LocalStorageKeys.CONNEXX_SHOW_TABLE_FILTERS),
      ) || true;

    // Phone-sized viewports start with the filter sidebar closed: open, it
    // stacks above the table (see Table.tsx) and would bury the results.
    const isMobileViewport = window.matchMedia("(max-width: 767px)").matches;

    dispatch(
      setFilterSidebarOpenStatus(isMobileViewport ? false : isTableFilterVisible),
    );
  }, []);

  // Set the timer once app is ready
  useEffect(() => {
    dispatch(setCurrentTimeUTC());
  }, []);

  // Update the timer every 30 seconds thereafter
  useInterval(() => {
    dispatch(setCurrentTimeUTC());
  }, 30000);

  return (
    <ReduxThemeProvider>
      <BugsnagErrorBoundary>
        <ErrorBoundary
          FallbackComponent={({ resetErrorBoundary }) => {
            return (
              <div className="w-screen h-screen">
                <Center>
                  <Error buttonLabel="Refresh" onClick={resetErrorBoundary} />
                </Center>
              </div>
            );
          }}
        >
          <DefaultSeo
            titleTemplate="%s - ITD Connexx"
            defaultTitle="ITD Connexx"
          />
          <Head>
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1"
            />
            {/* PNG first, SVG second: SVG-capable browsers pick the later entry,
                Safari (no SVG favicon support) falls back to the PNG. */}
            <link rel="icon" type="image/png" href="/images/favicon.png" />
            <link rel="icon" type="image/svg+xml" href="/images/favicon.svg" />
          </Head>
          {/*
            Zebra Browser Print SDK — exposes window.BrowserPrint (and window.Zebra)
            for the Scan & Print feature. The Zebra extension depends on the base SDK,
            so it is appended only after the base script has loaded.
          */}
          <Script
            src="/zebra/BrowserPrint-3.1.250.min.js"
            strategy="afterInteractive"
            onLoad={() => {
              const zebraScript = document.createElement("script");
              zebraScript.src = "/zebra/BrowserPrint-Zebra-1.1.250.min.js";
              zebraScript.async = true;
              document.body.appendChild(zebraScript);
            }}
          />
          <>
            {process.env.NEXT_PUBLIC_MAINTENANCE_MODE === "true" ? (
              <Maintenance />
            ) : (
              <>
                <Toaster />
                {/* Local boundary: if the lazy Modals chunk fails to load
                    (network blip), modals become unavailable until reload but
                    the rest of the app keeps working. */}
                <ErrorBoundary FallbackComponent={() => null}>
                  <Modals />
                </ErrorBoundary>
                <AnimatePresence exitBeforeEnter>
                  {isPublic ? (
                    <>
                      <PageWrapper
                        page={<Component {...pageProps} key={router?.route} />}
                      />
                      <Analytics />
                    </>
                  ) : (
                    <Auth0Provider
                      domain={
                        process.env.NEXT_PUBLIC_AUTH0_ISSUER_BASE_URL || ""
                      }
                      clientId={process.env.NEXT_PUBLIC_AUTH0_CLIENT_ID || ""}
                      redirectUri={process.env.NEXT_PUBLIC_AUTH0_BASE_URL}
                      scope={process.env.NEXT_PUBLIC_AUTH0_SCOPE}
                      audience={process.env.NEXT_PUBLIC_AUDIENCE}
                      useRefreshTokens
                      skipRedirectCallback={router.pathname.includes(
                        "/redirect",
                      )}
                    >
                      <LayoutAuthenticated>
                        <PageWrapper
                          page={
                            <Component {...pageProps} key={router?.route} />
                          }
                        />
                      </LayoutAuthenticated>
                      <Analytics />
                    </Auth0Provider>
                  )}
                </AnimatePresence>
              </>
            )}
          </>
        </ErrorBoundary>
      </BugsnagErrorBoundary>
    </ReduxThemeProvider>
  );
}

export default wrapper.withRedux(CustomApp);
